import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Button } from 'primereact/button';
import { isChunkLoadError } from '@/helpers/chunkLoadError.helpers';

interface BoundaryProps {
  children: ReactNode;
  /** The page's path: moving to another page clears the error. */
  resetKey: string;
}

interface RouteErrorBoundaryState {
  error: unknown;
}

/**
 * Keeps a page that fails to render from taking the whole app with it. Without
 * it, a page whose code did not download unmounted everything and left a blank
 * white screen, with no way back but a manual reload.
 */
class Boundary extends Component<BoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return { error };
  }

  // The router reuses this instance from page to page, so the error is
  // cleared on the move rather than by remounting — a remount would also drop
  // a chat that is streaming a reply while its URL changes.
  componentDidUpdate(previous: BoundaryProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[RouteErrorBoundary] a page failed to render:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const notDownloaded = isChunkLoadError(error);
    return (
      <div className='flex h-full w-full items-center justify-center p-8' role='alert'>
        <div className='max-w-md text-center'>
          <p className='mb-1 text-base font-semibold text-foreground dark:text-foreground-dark'>
            This page couldn’t be opened
          </p>
          <p className='mb-4 text-sm text-foreground/70 dark:text-foreground-dark/70'>
            {notDownloaded
              ? 'Part of the app didn’t download — the connection may have dropped, or a new version was released. Reloading fetches it again.'
              : 'Something went wrong while showing this page. Reloading usually clears it.'}
          </p>
          <div className='flex justify-center'>
            <Button
              type='button'
              label='Reload page'
              icon='pi pi-refresh'
              size='small'
              onClick={() => window.location.reload()}
            />
          </div>
        </div>
      </div>
    );
  }
}

const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
};

export default RouteErrorBoundary;
